import { useEffect } from "react";

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

export function isHexColor(v) {
  return HEX.test((v || "").trim());
}

export function normaliseHex(v) {
  const m = HEX.exec((v || "").trim());
  if (!m) return "";
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return `#${h.toUpperCase()}`;
}

export function hexToHsl(hex) {
  const h6 = normaliseHex(hex).slice(1);
  if (!h6) return null;
  const r = parseInt(h6.slice(0, 2), 16) / 255, g = parseInt(h6.slice(2, 4), 16) / 255, b = parseInt(h6.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function isDarkColor(hex) {
  const h6 = normaliseHex(hex).slice(1);
  if (!h6) return true;
  const r = parseInt(h6.slice(0, 2), 16), g = parseInt(h6.slice(2, 4), 16), b = parseInt(h6.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.6;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const hsl = (h, s, l) => `${h} ${s}% ${l}%`;

/* Tailwind tokens (--primary etc.) are HSL triplets; set them on `el` so bg-primary / text-primary pick up the brand.
   `mode` decides how the brand is kept legible (a navy brand still needs a visible selected item on a dark sidebar, a pale
   brand still needs readable links on white); `surfaces` additionally tints body / top bar / sidebar / cards with the
   brand hue — that's the client-portal treatment. */
export function brandVars(hex, mode = "light", { surfaces = false } = {}) {
  const c = hexToHsl(hex);
  if (!c) return {};
  const dark = mode === "dark";
  const pL = dark ? Math.max(c.l, 32) : Math.min(c.l, 88);   // primary fill: never vanishes into the background
  const inkL = dark ? Math.max(c.l, 62) : Math.min(c.l, 42);  // primary as text: always readable on the mode's surfaces
  const primary = hsl(c.h, c.s, pL);
  const onPrimary = (pL === c.l ? isDarkColor(hex) : pL < 55) ? "210 40% 98%" : "222 47% 11%";
  const vars = { "--primary": primary, "--ring": primary, "--brand": primary, "--primary-ink": hsl(c.h, c.s, inkL), "--primary-foreground": onPrimary };
  if (!surfaces) return vars;
  const tint = c.s < 12 ? 0 : 1; // grey brands get neutral surfaces rather than a phantom hue
  if (dark) {
    const sat = clamp(Math.round(c.s * 0.22), 0, 14) * tint;
    Object.assign(vars, {
      "--background": hsl(c.h, sat, 7), "--topbar": hsl(c.h, sat + 4, 9), "--sidebar": hsl(c.h, sat, 15), "--sidebar-bg": hsl(c.h, sat, 15),
      "--card": hsl(c.h, sat, 11), "--popover": hsl(c.h, sat, 11), "--muted": hsl(c.h, Math.max(sat - 4, 0), 20), "--secondary": hsl(c.h, Math.max(sat - 4, 0), 20),
      "--accent": hsl(c.h, Math.max(sat - 4, 0), 20), "--border": hsl(c.h, Math.max(sat - 4, 0), 19), "--input": hsl(c.h, Math.max(sat - 4, 0), 19), "--sidebar-border": hsl(c.h, Math.max(sat - 4, 0), 19),
    });
  } else {
    const sat = clamp(Math.round(c.s * 0.4), 0, 30) * tint;
    Object.assign(vars, {
      "--background": hsl(c.h, sat, 96), "--topbar": "0 0% 100%", "--sidebar": hsl(c.h, Math.min(sat + 8, 40), 97.5), "--sidebar-bg": hsl(c.h, Math.min(sat + 8, 40), 97.5),
      "--card": "0 0% 100%", "--popover": "0 0% 100%", "--muted": hsl(c.h, Math.round(sat * 0.8), 91), "--secondary": hsl(c.h, Math.round(sat * 0.8), 91),
      "--accent": hsl(c.h, Math.round(sat * 0.8), 91), "--border": hsl(c.h, Math.round(sat * 0.7), 86), "--input": hsl(c.h, Math.round(sat * 0.7), 86), "--sidebar-border": hsl(c.h, Math.round(sat * 0.7), 86),
    });
  }
  return vars;
}

export function applyBrandColor(hex, el = document.documentElement, opts) {
  const vars = brandVars(hex, opts?.mode, opts);
  const keys = Object.keys(vars);
  if (!keys.length) return () => {};
  const prev = keys.map((k) => [k, el.style.getPropertyValue(k)]);
  keys.forEach((k) => el.style.setProperty(k, vars[k]));
  window.dispatchEvent(new Event("fixlr:brand"));
  return () => { prev.forEach(([k, v]) => (v ? el.style.setProperty(k, v) : el.style.removeProperty(k))); window.dispatchEvent(new Event("fixlr:brand")); };
}

export function useBrandColor(hex, opts) {
  const mode = opts?.mode, surfaces = !!opts?.surfaces;
  useEffect(() => (hex ? applyBrandColor(hex, document.documentElement, { mode, surfaces }) : undefined), [hex, mode, surfaces]);
}
